/**
 * @fileoverview API Client for Soma Business Management Platform
 * @description Centralized API client with authentication, error handling, and comprehensive endpoint coverage
 */

import { bulkImportAPI } from './bulkImportAPI.js';
import { secureStorage } from './storage.js';

// API Configuration
const API_CONFIG = {
  baseURL: `${(import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/api/v1`,
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  cacheTTL: 30000,
};

// Storage keys for authentication
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'soma_access_token',
  REFRESH_TOKEN: 'soma_refresh_token',
  USER_DATA: 'soma_user_data',
};

/**
 * API Error class for structured error handling
 */
class APIError extends Error {
  constructor(message, status, code, details = null) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Normalize API response data by removing $ prefix from keys
 * (e.g., $id -> id, $createdAt -> createdAt)
 */
const normalizeData = (data) => {
  if (Array.isArray(data)) {
    return data.map(item => normalizeData(item));
  }
  if (data !== null && typeof data === 'object') {
    const newData = {};
    Object.keys(data).forEach(key => {
      const newKey = key.startsWith('$') ? key.slice(1) : key;
      newData[newKey] = normalizeData(data[key]);
    });
    return newData;
  }
  return data;
};

/**
 * Main API Client Class
 */
class APIClient {
  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.timeout = API_CONFIG.timeout;
    this.retryAttempts = API_CONFIG.retryAttempts;
    this.retryDelay = API_CONFIG.retryDelay;
    this.cache = new Map();
    this.pending = new Map();
    this._refreshPromise = null;
    
    // Request interceptors
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    
    // Setup default interceptors
    this.setupDefaultInterceptors();

    // Add response normalizer
    this.addResponseInterceptor((response) => {
      if (response && response.data) {
        response.data = normalizeData(response.data);
      } else if (response) {
        // If response is the data itself (some endpoints might return direct array/object)
        // But usually our API wraps in { success, data }
        // We'll normalize the whole response just in case, but usually we target .data
        // actually, let's just stick to response.data normalization to be safe
      }
      return response;
    });
  }

  /**
   * Get user data from storage
   */
  getUserData() {
    return secureStorage.getItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Setup default interceptors for authentication and tenant context
   */
  setupDefaultInterceptors() {
    // Add auth token and tenant context to requests
    this.addRequestInterceptor((config) => {
      const token = this.getAccessToken();
      const userData = this.getUserData();
      
      if (token) {
        config.headers = {
          ...config.headers,
          'Authorization': `Bearer ${token}`,
        };
      }

      // Add tenant context headers if user data is available
      if (userData) {
        config.headers = {
          ...config.headers,
          'X-User-ID': userData.id || userData._id,
          'X-Business-Type': userData.businessType || userData.business?.type,
        };

        // Add tenant ID if available
        if (userData.tenantId || userData.tenant?.id) {
          config.headers['X-Tenant-ID'] = userData.tenantId || userData.tenant.id;
        }
      }

      return config;
    });

    // Handle token refresh on 401 responses
    this._authRedirected = false;
    this.addResponseInterceptor(
      (response) => response,
      async (error) => {
        const requestUrl = error?.config?.url || '';
        const isRefreshRequest = typeof requestUrl === 'string' && requestUrl.includes('/auth/refresh');
        const refreshToken = this.getRefreshToken();

        if (error.status === 401 && isRefreshRequest) {
          this.clearAuthData();
          if (!this._authRedirected) {
            this._authRedirected = true;
            window.location.href = '/login';
          }
          throw error;
        }

        if (error.status === 401 && !refreshToken) {
          this.clearAuthData();
          if (!this._authRedirected) {
            this._authRedirected = true;
            window.location.href = '/login';
          }
          throw error;
        }

        if (error.status === 401 && refreshToken && !error?.config?._retry) {
          try {
            error.config._retry = true;
            if (!this._refreshPromise) {
              this._refreshPromise = this.refreshAccessToken().finally(() => {
                this._refreshPromise = null;
              });
            }
            await this._refreshPromise;
            return this.request(error.config);
          } catch (refreshError) {
            this.clearAuthData();
            if (!this._authRedirected) {
              this._authRedirected = true;
              window.location.href = '/login';
            }
            throw refreshError;
          }
        }
        throw error;
      }
    );
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(onSuccess, onError) {
    this.responseInterceptors.push({ onSuccess, onError });
  }

  serializeParams(params) {
    if (!params) return '';
    const entries = [];
    Object.keys(params).forEach((key) => {
      const val = params[key];
      if (val === undefined || val === null || val === '') return;
      if (Array.isArray(val)) {
        val.forEach((v) => entries.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`));
      } else if (typeof val === 'object') {
        Object.keys(val).forEach((sub) => {
          const v = val[sub];
          if (v !== undefined && v !== null && v !== '') {
            entries.push(`${encodeURIComponent(`${key}.${sub}`)}=${encodeURIComponent(String(v))}`);
          }
        });
      } else {
        entries.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`);
      }
    });
    return entries.length ? `?${entries.join('&')}` : '';
  }

  /**
   * Get access token from storage
   */
  getAccessToken() {
    return secureStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get refresh token from storage
   */
  getRefreshToken() {
    return secureStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Set authentication tokens
   */
  setAuthTokens(accessToken, refreshToken) {
    secureStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    if (refreshToken) {
      secureStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }
  }

  /**
   * Clear authentication data
   */
  clearAuthData() {
    secureStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    secureStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    secureStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new APIError('No refresh token available', 401, 'NO_REFRESH_TOKEN');
    }

    const response = await this.request({
      method: 'POST',
      url: '/auth/refresh',
      data: { refreshToken },
    });

    if (response.success && response.data.accessToken) {
      this.setAuthTokens(response.data.accessToken, response.data.refreshToken);
      return response.data.accessToken;
    }

    throw new APIError('Failed to refresh token', 401, 'REFRESH_FAILED');
  }

  /**
   * Main request method with retry logic and interceptors
   */
  async request(config) {
    // Apply request interceptors
    let processedConfig = { ...config };
    for (const interceptor of this.requestInterceptors) {
      processedConfig = interceptor(processedConfig);
    }

    // Set default headers
    processedConfig.headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...processedConfig.headers,
    };

    let url = processedConfig.url.startsWith('http') 
      ? processedConfig.url 
      : `${this.baseURL}${processedConfig.url}`;
    if (processedConfig.params) {
      url += this.serializeParams(processedConfig.params);
    }

    let lastError;
    
    // Retry logic
    for (let attempt = 0; attempt <= this.retryAttempts; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutMs = processedConfig.timeout ?? this.timeout;
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const fetchOptions = {
          method: processedConfig.method || 'GET',
          headers: processedConfig.headers,
          signal: controller.signal,
        };

        if (fetchOptions.method !== 'GET') {
          const csrf = (typeof window !== 'undefined') ? sessionStorage.getItem('csrf_token') : null;
          if (csrf) {
            fetchOptions.headers['X-CSRF-Token'] = csrf;
          }
        }

        // Add body for non-GET requests
        if (processedConfig.data && processedConfig.method !== 'GET') {
          if (processedConfig.data instanceof FormData) {
            fetchOptions.body = processedConfig.data;
            // Remove Content-Type header for FormData (browser will set it)
            delete fetchOptions.headers['Content-Type'];
          } else {
            fetchOptions.body = JSON.stringify(processedConfig.data);
          }
        }

        // Simplified request without caching to prevent hanging
        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);

        let responseData;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          responseData = await response.text();
        }

        if (!response.ok) {
          const error = new APIError(
            responseData.message || `HTTP ${response.status}`,
            response.status,
            responseData.code || 'HTTP_ERROR',
            responseData
          );
          error.config = processedConfig;
          
          // Apply response error interceptors
          for (const interceptor of this.responseInterceptors) {
            if (interceptor.onError) {
              try {
                return await interceptor.onError(error);
              } catch (interceptorError) {
                throw interceptorError;
              }
            }
          }
          
          throw error;
        }

        // Apply response success interceptors
        let processedResponse = responseData;
        for (const interceptor of this.responseInterceptors) {
          if (interceptor.onSuccess) {
            processedResponse = interceptor.onSuccess(processedResponse);
          }
        }

        // Return response without caching to prevent hanging
        return processedResponse;

      } catch (error) {
        lastError = error;
        
        // Don't retry on certain errors
        if (error.name === 'AbortError' || 
            error.status === 401 || 
            error.status === 403 || 
            error.status === 422) {
          throw error;
        }

        // Wait before retry
        if (attempt < this.retryAttempts) {
          await new Promise(resolve => setTimeout(resolve, this.retryDelay * (attempt + 1)));
        }
      }
    }

    throw lastError;
  }

  /**
   * HTTP method shortcuts
   */
  get(url, config = {}) {
    return this.request({ ...config, method: 'GET', url });
  }

  post(url, data, config = {}) {
    return this.request({ ...config, method: 'POST', url, data });
  }

  put(url, data, config = {}) {
    return this.request({ ...config, method: 'PUT', url, data });
  }

  patch(url, data, config = {}) {
    return this.request({ ...config, method: 'PATCH', url, data });
  }

  delete(url, config = {}) {
    return this.request({ ...config, method: 'DELETE', url });
  }
}

// Create API client instance
const apiClient = new APIClient();

// Export apiClient for use in other modules
export { apiClient };

/**
 * Authentication API
 */
export const authAPI = {
  /**
   * User login
   */
  async login(credentials) {
    const response = await apiClient.post('/auth/login', credentials);
    
    if (response.success && response.data.tokens) {
      apiClient.setAuthTokens(
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );
      
      // Store user data
      if (response.data.user) {
        secureStorage.setItem(STORAGE_KEYS.USER_DATA, response.data.user);
      }
    }
    
    return response;
  },

  /**
   * User registration
   */
  async register(userData) {
    const response = await apiClient.post('/auth/register', userData);
    
    if (response.success && response.data.tokens) {
      apiClient.setAuthTokens(
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );
      
      if (response.data.user) {
        secureStorage.setItem(STORAGE_KEYS.USER_DATA, response.data.user);
      }
    }
    
    return response;
  },

  /**
   * User logout
   */
  async logout() {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      apiClient.clearAuthData();
    }
  },

  /**
   * Get current user
   */
  async getCurrentUser() {
    try {
      return await apiClient.get('/auth/me', { timeout: 5000 }); // 5 second timeout
    } catch (error) {
      // If API is not available or times out, return a mock response
      if (error.name === 'AbortError' || error.message?.includes('timeout') || error.status >= 500) {
        console.warn('Auth API unavailable, returning mock response');
        return {
          success: false,
          data: null,
          error: 'Service temporarily unavailable'
        };
      }
      throw error;
    }
  },

  /**
   * Change password
   */
  async changePassword(passwordData) {
    return apiClient.post('/auth/change-password', passwordData);
  },

  /**
   * Forgot password
   */
  async forgotPassword(email) {
    return apiClient.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password
   */
  async resetPassword(resetData) {
    return apiClient.post('/auth/reset-password', resetData);
  },

  /**
   * Complete onboarding
   */
  async completeOnboarding(onboardingData) {
    return apiClient.post('/auth/complete-onboarding', onboardingData);
  },

  /**
   * Get session info
   */
  async getSession() {
    return apiClient.get('/auth/session');
  },

  /**
   * Two-Factor Authentication
   */
  enable2FA: () => apiClient.post('/auth/2fa/enable'),
  disable2FA: () => apiClient.post('/auth/2fa/disable'),
  verify2FA: (token, secret) => apiClient.post('/auth/2fa/verify', { token, secret }),
};

/**
 * CA API
 */
export const caAPI = {
  /**
   * Public CA Registration
   */
  async register(userData) {
    const response = await apiClient.post('/ca/public-register', userData);
    
    if (response.success && response.data.tokens) {
      apiClient.setAuthTokens(
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );
      
      if (response.data.user) {
        secureStorage.setItem(STORAGE_KEYS.USER_DATA, response.data.user);
      }
    }
    
    return response;
  },

  getProfile: () => apiClient.get('/ca/profile'),
  updateProfile: (data) => apiClient.post('/ca/profile', data),
  getClients: () => apiClient.get('/ca/clients'),
  addClient: (data) => apiClient.post('/ca/clients', data),
  
  // Team Members
  getTeamMembers: () => apiClient.get('/ca/team'),
  inviteTeamMember: (data) => apiClient.post('/ca/team/invite', data),
  removeTeamMember: (memberId) => apiClient.delete(`/ca/team/${memberId}`),
  updateTeamMemberRole: (memberId, role) => apiClient.patch(`/ca/team/${memberId}`, { role }),

  // Billing & Plan (Using Subscription Routes)
  getSubscription: () => apiClient.get('/subscriptions/current'),
  getPlans: () => apiClient.get('/subscriptions/plans'),
  getBillingHistory: () => apiClient.get('/subscriptions/billing-history'),
  upgradePlan: (data) => apiClient.post('/subscriptions/upgrade', data),
  cancelSubscription: (data) => apiClient.post('/subscriptions/cancel', data),

  // Security
  changePassword: (data) => apiClient.post('/auth/change-password', data),
  enable2FA: () => apiClient.post('/auth/2fa/enable'),
  disable2FA: () => apiClient.post('/auth/2fa/disable'),
  verify2FA: (token, secret) => apiClient.post('/auth/2fa/verify', { token, secret }),

  // Integrations
  getIntegrationsStatus: () => apiClient.get('/integrations/status'),
  enableIntegration: (type) => apiClient.post(`/integrations/${type}/enable`),
  disableIntegration: (type) => apiClient.post(`/integrations/${type}/disable`),
  configureIntegration: (type, config) => apiClient.post(`/integrations/${type}/configure`, config),
  getInvites: () => apiClient.get('/ca/invites'),
  acceptInvite: (linkId) => apiClient.post(`/ca/invites/${linkId}/accept`),
  verifyInvitationToken: (token) => apiClient.get(`/ca/invitation/verify/${token}`, {}, { requireAuth: false }),
  registerWithInvite: (data) => apiClient.post('/ca/register', data, { requireAuth: false }),
  revokeAccess: (linkId) => apiClient.post('/ca/revoke', { linkId }),
  getClientMIS: (businessId, params) => apiClient.get(`/ca/clients/${businessId}/mis`, { params }),
  getClientTrends: (businessId, startDate, endDate) => apiClient.get(`/ca/clients/${businessId}/trends`, { params: { startDate, endDate } }),
  getClientComplianceCalendar: (businessId, year, month) => apiClient.get(`/ca/clients/${businessId}/calendar`, { params: { year, month } }),
  getClientDetail: (businessId) => apiClient.get(`/ca/clients/${businessId}`),
  getClientFilingHistory: (businessId, limit = 50) => apiClient.get(`/ca/clients/${businessId}/filings`, { params: { limit } }),
  getClientDocuments: (businessId, type = null) => apiClient.get(`/ca/clients/${businessId}/documents`, { params: { type } }),
  getClientTransactions: (businessId, type = 'all', limit = 50) => apiClient.get(`/ca/clients/${businessId}/transactions`, { params: { type, limit } }),
  saveClientNote: (businessId, note, type = 'note') => apiClient.post(`/ca/clients/${businessId}/notes`, { note, type }),
  updateClientSettings: (businessId, settings) => apiClient.put(`/ca/clients/${businessId}/settings`, settings),
  inviteCA: (businessId, email) => apiClient.post(`/business/${businessId}/invite-ca`, { email }),
  
  // CSV Upload
  uploadOrdersCSV: (businessId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/ca/clients/${businessId}/upload/orders`, formData);
  },
  uploadPurchasesCSV: (businessId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/ca/clients/${businessId}/upload/purchases`, formData);
  },
  downloadCSVTemplate: async (businessId, type) => {
    const response = await fetch(
      `${API_CONFIG.baseURL}/ca/clients/${businessId}/upload/template/${type}`,
      {
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)}`
        }
      }
    );
    if (!response.ok) throw new Error('Failed to download template');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return { success: true };
  },
  
  // Compliance
  getComplianceCalendar: (month, year) => apiClient.get('/ca/compliance/calendar', { params: { month, year } }),
  
  // Dashboard methods
  getDashboardMetrics: () => apiClient.get('/ca/dashboard/metrics'),
  getDashboardActivity: (limit = 20) => apiClient.get('/ca/dashboard/activity', { params: { limit } }),
  getDashboardRevenue: () => apiClient.get('/ca/dashboard/revenue'),
  getDashboardPerformance: () => apiClient.get('/ca/dashboard/performance'),
  getDashboardHealth: () => apiClient.get('/ca/dashboard/health'),
  getNotices: (status) => apiClient.get('/ca/compliance/notices', { params: { status } }),
  createNotice: (data) => apiClient.post('/ca/compliance/notices', data),
  
  // Documents
  getDocuments: (params) => apiClient.get('/ca/documents', { params }),
  uploadDocument: (data) => apiClient.post('/ca/documents', data),
  getFolders: (parentId) => apiClient.get('/ca/folders', { params: { parentId } }),
  createFolder: (data) => apiClient.post('/ca/folders', data),
  
  // Document Access
  getDocumentDownloadUrl: (fileId) => {
    const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT || 'https://cloud.appwrite.io/v1';
    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID;
    const bucketId = import.meta.env.VITE_APPWRITE_STORAGE_BUCKET_ID || 'soma';
    return `${endpoint}/storage/buckets/${bucketId}/files/${fileId}/download?project=${projectId}&mode=admin`;
  },
  
  getDocumentViewUrl: (fileId) => {
    const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT || 'https://cloud.appwrite.io/v1';
    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID;
    const bucketId = import.meta.env.VITE_APPWRITE_STORAGE_BUCKET_ID || 'soma';
    return `${endpoint}/storage/buckets/${bucketId}/files/${fileId}/view?project=${projectId}&mode=admin`;
  },

  // Verification & Returns
  verifyGSTIN: (gstin) => apiClient.post('/ca/gstin/verify', { gstin }, { timeout: 90000 }), // 90 seconds for GSTIN verification
  parseReturnFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/ca/returns/parse', formData);
  },
  
  // Client Data Management
  getClientFinancialData: (businessId, params) => apiClient.get(`/ca/clients/${businessId}/financial-data`, { params }),
  editClientInvoice: (businessId, invoiceId, updates) => apiClient.patch(`/ca/clients/${businessId}/invoices/${invoiceId}/edit`, updates),
  bulkEditClientInvoices: (businessId, updates) => apiClient.post(`/ca/clients/${businessId}/invoices/bulk-edit`, { updates }),
  validateClientInvoiceData: (businessId, invoiceId) => apiClient.post(`/ca/clients/${businessId}/invoices/${invoiceId}/validate`),
  validateClientReturn: (businessId, returnData, returnType) => {
    // Normalize returnType: remove hyphens and uppercase
    const normalizedReturnType = returnType ? returnType.replace(/-/g, '').toUpperCase() : returnType;
    return apiClient.post(`/ca/clients/${businessId}/returns/validate`, { returnData, returnType: normalizedReturnType });
  },
  getValidationResult: (businessId, returnType) => {
    const normalizedReturnType = returnType ? returnType.replace(/-/g, '').toUpperCase() : returnType;
    return apiClient.get(`/ca/clients/${businessId}/returns/validate`, { params: { returnType: normalizedReturnType } });
  },
  
  // Reports
  generateClientReport: (businessId, reportType, params) => apiClient.post(`/ca/clients/${businessId}/reports/${reportType}`, params),
  exportClientReport: (businessId, reportType, format, params) => apiClient.post(`/ca/clients/${businessId}/reports/${reportType}/export/${format}`, params),
  
  // ========== Phase 1: GSTR-1 Document Retrieval ==========
  getClientGSTR1Summary: (businessId, period, options = {}) => apiClient.get(`/ca/clients/${businessId}/returns/gstr1/${period}/summary`, { params: options }),
  getClientGSTR1Document: (businessId, period, docType, options = {}) => apiClient.get(`/ca/clients/${businessId}/returns/gstr1/${period}/documents/${docType}`, { params: options }),
  
  // ========== Phase 2: GSTR-3B Complete Implementation ==========
  getClientGSTR3BDetails: (businessId, period) => apiClient.get(`/ca/clients/${businessId}/returns/gstr3b/${period}/details`),
  saveClientGSTR3B: (businessId, period, returnData) => apiClient.post(`/ca/clients/${businessId}/returns/gstr3b/${period}/save`, returnData),
  validateClientGSTR3B: (businessId, period) => apiClient.post(`/ca/clients/${businessId}/returns/gstr3b/${period}/validate`),
  getClientGSTR3BAutoLiability: (businessId, period) => apiClient.get(`/ca/clients/${businessId}/returns/gstr3b/${period}/auto-liability`),
  offsetClientGSTR3BLiability: (businessId, period, offsetData) => apiClient.post(`/ca/clients/${businessId}/returns/gstr3b/${period}/offset-liability`, offsetData),
  
  // ========== Phase 3: GSTR-2A/2B Full Document Access ==========
  getClientGSTR2ADocument: (businessId, period, docType, options = {}) => apiClient.get(`/ca/clients/${businessId}/reconciliation/gstr2a/${period}/documents/${docType}`, { params: options }),
  getClientGSTR2BDocument: (businessId, period) => apiClient.get(`/ca/clients/${businessId}/reconciliation/gstr2b/${period}/document`),
  
  // ========== Phase 4: Ledgers Implementation ==========
  getClientLedger: (businessId, ledgerType, options = {}) => apiClient.get(`/ca/clients/${businessId}/ledgers/${ledgerType}`, { params: options }),
  getClientCashITCBalance: (businessId, period = null) => apiClient.get(`/ca/clients/${businessId}/ledgers/cash-itc-balance`, { params: period ? { period } : {} }),
  
  // ========== Phase 5: Return Status & Preferences ==========
  getClientReturnStatusImproved: (businessId, returnType, period, referenceId = null) => apiClient.get(`/ca/clients/${businessId}/returns/${returnType}/${period}/status`, { params: referenceId ? { reference_id: referenceId } : {} }),
  saveClientFilingPreference: (businessId, preferenceData) => apiClient.post(`/ca/clients/${businessId}/preferences`, preferenceData),
  getClientFilingPreference: (businessId) => apiClient.get(`/ca/clients/${businessId}/preferences`),
  
  // ========== Phase 6: EVC OTP & Filing Support ==========
  generateClientEVCOTP: (businessId, returnType, period) => apiClient.post(`/ca/clients/${businessId}/returns/${returnType}/${period}/evc-otp`),
  proceedToFileClientGSTR1: (businessId, period) => apiClient.post(`/ca/clients/${businessId}/returns/gstr1/${period}/proceed-to-file`),
  
  // Existing filing methods (already implemented)
  saveClientReturnDraft: (businessId, returnType, period, returnData, summary) => apiClient.post(`/ca/clients/${businessId}/returns/save`, { returnType, period, returnData, summary }),
  fileClientReturn: (businessId, returnType, period, returnData, options = {}) => apiClient.post(`/ca/clients/${businessId}/returns/file`, { returnType, period, returnData, ...options }),
  getClientFilingStatus: (businessId, filingId) => apiClient.get(`/ca/clients/${businessId}/returns/status/${filingId}`),
  
  // Existing reconciliation methods (already implemented)
  reconcileGSTR2AForCA: (businessId, period, options = {}) => apiClient.post(`/ca/clients/${businessId}/reconciliation/gstr2a`, { period, ...options }),
  reconcileGSTR2BForCA: (businessId, period, options = {}) => apiClient.post(`/ca/clients/${businessId}/reconciliation/gstr2b`, { period, ...options }),
  getClientReconciliationHistory: (businessId, limit = 50) => apiClient.get(`/ca/clients/${businessId}/reconciliation/history`, { params: { limit } }),
  
  // Existing analysis methods (already implemented)
  getGSTAnalysisForCA: (businessId, period) => apiClient.get(`/ca/clients/${businessId}/analysis/gst`, { params: { period } }),
  getComplianceAnalysisForCA: (businessId, year) => apiClient.get(`/ca/clients/${businessId}/analysis/compliance`, { params: { year } }),
};

/**
 * Analytics API
 */
export const analyticsAPI = {
  /**
   * Get dashboard analytics
   */
  async getDashboard(params = {}) {
    return apiClient.get('/analytics/dashboard', { params });
  },

  /**
   * Get sales analytics
   */
  async getSalesAnalytics() {
    return apiClient.get('/analytics/sales');
  },

  /**
   * Get inventory analytics
   */
  async getInventoryAnalytics() {
    return apiClient.get('/analytics/inventory');
  },

  /**
   * Get customer analytics
   */
  async getCustomerAnalytics() {
    return apiClient.get('/analytics/customers');
  },

  /**
   * Get financial analytics
   */
  async getFinancialAnalytics() {
    return apiClient.get('/analytics/financial');
  },

  /**
   * Get performance analytics
   */
  async getPerformanceAnalytics() {
    return apiClient.get('/analytics/performance');
  },

  /**
   * Get trends analytics
   */
  async getTrendsAnalytics() {
    return apiClient.get('/analytics/trends');
  },

  /**
   * Get sales trend chart
   */
  async getSalesTrendChart() {
    return apiClient.get('/charts/sales-trend');
  },

  /**
   * Get inventory status chart
   */
  async getInventoryStatusChart() {
    return apiClient.get('/charts/inventory-status');
  },

  /**
   * Get comparisons analytics
   */
  async getComparisonsAnalytics() {
    return apiClient.get('/analytics/comparisons');
  },

  /**
   * Get predictions analytics
   */
  async getPredictionsAnalytics() {
    return apiClient.get('/analytics/predictions');
  },

  /**
   * Get real-time analytics
   */
  async getRealTimeAnalytics() {
    return apiClient.get('/analytics/real-time');
  },

  /**
   * Get analytics config
   */
  async getConfigAnalytics() {
    return apiClient.get('/analytics/config');
  },
  
  // Legacy support if needed
  async getAdvancedAnalytics(params = {}) {
    return apiClient.get('/advanced-analytics/insights/summary', { params });
  },
};

/**
 * Business API
 */
export const businessAPI = {
  /**
   * Create business profile
   */
  async createProfile(profileData) {
    return apiClient.post('/business/profile', profileData);
  },

  /**
   * Get business profile
   */
  async getProfile() {
    return apiClient.get('/business/profile');
  },

  /**
   * Update business profile
   */
  async updateProfile(profileData) {
    return apiClient.put('/business/profile', profileData);
  },

  /**
   * Get business settings
   */
  async getSettings() {
    return apiClient.get('/business/settings');
  },

  /**
   * Update business settings
   */
  async updateSettings(settingsData) {
    return apiClient.put('/business/settings', settingsData);
  },

  /**
   * Get business types
   */
  async getBusinessTypes() {
    return apiClient.get('/business/types');
  },

  /**
   * Get authorized CAs
   */
  async getAuthorizedCAs(businessId) {
    return apiClient.get(`/business/${businessId}/authorized-cas`);
  },

  /**
   * Get custom fields for a business type and entity type
   */
  async getCustomFields(businessType, entityType) {
    return apiClient.get(`/business/types/${businessType}/custom-fields/${entityType}`);
  },

  /**
   * Validate custom fields
   */
  async validateCustomFields(businessType, entityType, customFields) {
    return apiClient.post(`/business/types/${businessType}/validate-custom-fields/${entityType}`, {
      customFields
    });
  },
};

/**
 * Vendor Team API
 */
export const vendorTeamAPI = {
  /**
   * Get all team members for business
   */
  async getTeamMembers() {
    return apiClient.get('/business/team');
  },

  /**
   * Invite a team member
   */
  async inviteTeamMember(data) {
    return apiClient.post('/business/team/invite', data);
  },

  /**
   * Update team member role
   */
  async updateTeamMemberRole(memberId, role) {
    return apiClient.patch(`/business/team/${memberId}`, { role });
  },

  /**
   * Remove team member
   */
  async removeTeamMember(memberId) {
    return apiClient.delete(`/business/team/${memberId}`);
  },

  /**
   * Accept team invitation
   */
  async acceptInvitation(email) {
    return apiClient.post('/business/team/accept', { email });
  },
};

/**
 * Inventory API
 */
export const inventoryAPI = {
  /**
   * Get all inventory items
   */
  async getItems(params = {}) {
    return apiClient.get('/inventory', { params });
  },

  /**
   * Get single inventory item
   */
  async getItem(itemId) {
    return apiClient.get(`/inventory/${itemId}`);
  },

  /**
   * Create inventory item
   */
  async createItem(itemData) {
    return apiClient.post('/inventory', itemData);
  },

  /**
   * Update inventory item
   */
  async updateItem(itemId, itemData) {
    return apiClient.put(`/inventory/${itemId}`, itemData);
  },

  /**
   * Delete inventory item
   */
  async deleteItem(itemId) {
    return apiClient.delete(`/inventory/${itemId}`);
  },

  /**
   * Bulk create inventory items
   */
  async bulkCreate(itemsData) {
    return apiClient.post('/inventory/bulk/create', itemsData);
  },

  /**
   * Bulk update inventory items
   */
  async bulkUpdate(updateData) {
    return apiClient.put('/inventory/bulk/update', updateData);
  },

  /**
   * Bulk delete inventory items
   */
  async bulkDelete(idsData) {
    return apiClient.delete('/inventory/bulk/delete', { data: idsData });
  },

  /**
   * Bulk stock update
   */
  async bulkStockUpdate(stockUpdates) {
    return apiClient.post('/inventory/bulk/stock-update', stockUpdates);
  },

  /**
   * Get low stock items
   */
  async getLowStock(params = {}) {
    return apiClient.get('/inventory/low-stock', { params });
  },

  async getExpiring(params = {}) {
    return apiClient.get('/inventory/expiring', { params });
  },

  /**
   * Get inventory statistics
   */
  async getStats() {
    return apiClient.get('/inventory/stats');
  },

  /**
   * Adjust stock
   */
  async adjustStock(adjustmentData) {
    return apiClient.post('/inventory/adjust-stock', adjustmentData);
  },
};

/**
 * Orders API
 */
export const ordersAPI = {
  /**
   * Get all orders
   */
  async getOrders(params = {}) {
    return apiClient.get('/orders', { params });
  },

  /**
   * Get single order
   */
  async getOrder(orderId) {
    return apiClient.get(`/orders/${orderId}`);
  },

  /**
   * Create order
   */
  async createOrder(orderData) {
    return apiClient.post('/orders', orderData);
  },

  /**
   * Update order
   */
  async updateOrder(orderId, orderData) {
    return apiClient.put(`/orders/${orderId}`, orderData);
  },

  /**
   * Delete order
   */
  async deleteOrder(orderId) {
    return apiClient.delete(`/orders/${orderId}`);
  },

  /**
   * Update order status
   */
  async updateStatus(orderId, status) {
    return apiClient.patch(`/orders/${orderId}/status`, { status });
  },

  /**
   * Get order statistics
   */
  async getStats() {
    return apiClient.get('/orders/stats');
  },

  /**
   * Get urgent orders
   */
  async getUrgentOrders() {
    return apiClient.get('/orders/urgent');
  },

  /**
   * Get payment history for an order
   */
  async getPayments(orderId) {
    return apiClient.get(`/orders/${orderId}/payments`);
  },

  /**
   * Record payment for an order
   */
  async recordPayment(orderId, paymentData) {
    return apiClient.post(`/orders/${orderId}/payment`, paymentData);
  },

  /**
   * Cancel order
   */
  async cancelOrder(orderId) {
    return apiClient.post(`/orders/${orderId}/cancel`);
  },

  /**
   * Get orders for a specific customer
   */
  async getCustomerOrders(customerId, params = {}) {
    return apiClient.get(`/orders/customer/${customerId}`, { params });
  },

  /**
   * Create multiple orders in bulk
   */
  async bulkCreateOrders(ordersData) {
    return apiClient.post('/orders/bulk/create', ordersData);
  },

  /**
   * Update multiple orders in bulk
   */
  async bulkUpdateOrders(updatesData) {
    return apiClient.put('/orders/bulk/update', updatesData);
  },

  /**
   * Cancel multiple orders in bulk
   */
  async bulkCancelOrders(orderIds, reason) {
    return apiClient.post('/orders/bulk-cancel', { orderIds, reason });
  },
};

/**
 * Customers API
 */
export const customersAPI = {
  /**
   * Get all customers
   */
  async getCustomers(params = {}) {
    return apiClient.get('/customers', { params });
  },

  /**
   * Get single customer
   */
  async getCustomer(customerId) {
    return apiClient.get(`/customers/${customerId}`);
  },

  /**
   * Create customer
   */
  async createCustomer(customerData) {
    return apiClient.post('/customers', customerData);
  },

  /**
   * Update customer
   */
  async updateCustomer(customerId, customerData) {
    return apiClient.put(`/customers/${customerId}`, customerData);
  },

  /**
   * Delete customer
   */
  async deleteCustomer(customerId) {
    return apiClient.delete(`/customers/${customerId}`);
  },

  /**
   * Get customers with overdue amounts
   */
  async getOverdueCustomers(params = {}) {
    return apiClient.get('/customers/overdue', { params });
  },

  /**
   * Update customer due amount
   */
  async updateDue(customerId, dueData) {
    return apiClient.patch(`/customers/${customerId}/due`, dueData);
  },

  /**
   * Record payment for customer
   */
  async recordPayment(customerId, paymentData) {
    return apiClient.post(`/customers/${customerId}/payment`, paymentData);
  },

  /**
   * Bulk create customers
   */
  async bulkCreate(customersData) {
    return apiClient.post('/customers/bulk', customersData);
  },

  /**
   * Bulk update customers
   */
  async bulkUpdate(updatesData) {
    return apiClient.put('/customers/bulk', updatesData);
  },

  /**
   * Get customer balances for a specific date
   */
  async getCustomerBalances(date) {
    return apiClient.get('/customers/balances', { params: { date } });
  },
};

/**
 * Suppliers API
 */
export const suppliersAPI = {
  /**
   * Get all suppliers
   */
  async getSuppliers(params = {}) {
    return apiClient.get('/suppliers', { params });
  },

  /**
   * Get single supplier
   */
  async getSupplier(supplierId) {
    return apiClient.get(`/suppliers/${supplierId}`);
  },

  /**
   * Create supplier
   */
  async createSupplier(supplierData) {
    return apiClient.post('/suppliers', supplierData);
  },

  /**
   * Update supplier
   */
  async updateSupplier(supplierId, supplierData) {
    return apiClient.put(`/suppliers/${supplierId}`, supplierData);
  },

  /**
   * Delete supplier
   */
  async deleteSupplier(supplierId) {
    return apiClient.delete(`/suppliers/${supplierId}`);
  },

  /**
   * Get supplier statistics
   */
  async getStats() {
    return apiClient.get('/suppliers/stats');
  },
};

/**
 * Purchases API
 */
export const purchasesAPI = {
  /**
   * Get all purchases
   */
  async getPurchases(params = {}) {
    return apiClient.get('/purchases', { params });
  },

  /**
   * Get single purchase
   */
  async getPurchase(purchaseId) {
    return apiClient.get(`/purchases/${purchaseId}`);
  },

  /**
   * Create purchase
   */
  async createPurchase(purchaseData) {
    return apiClient.post('/purchases', purchaseData);
  },

  /**
   * Update purchase
   */
  async updatePurchase(purchaseId, purchaseData) {
    return apiClient.put(`/purchases/${purchaseId}`, purchaseData);
  },

  /**
   * Delete purchase
   */
  async deletePurchase(purchaseId) {
    return apiClient.delete(`/purchases/${purchaseId}`);
  },

  /**
   * Get purchase statistics
   */
  async getStats() {
    return apiClient.get('/purchases/stats');
  },

  /**
   * Get overdue purchases
   */
  async getOverdue() {
    return apiClient.get('/purchases/overdue');
  },

  /**
   * Record payment for purchase
   */
  async recordPayment(purchaseId, paymentData) {
    return apiClient.post(`/purchases/${purchaseId}/payment`, paymentData);
  },
};

/**
 * Invoices API
 */
export const invoicesAPI = {
  /**
   * Get paginated list of invoices
   */
  async getInvoices(params = {}) {
    return apiClient.get('/invoices', { params });
  },

  /**
   * Get invoice OCR credits and usage information
   */
  async getCredits() {
    return apiClient.get('/invoices/credits');
  },

  /**
   * Get single invoice with OCR / GST details
   */
  async getInvoice(invoiceId) {
    return apiClient.get(`/invoices/${invoiceId}`);
  },

  /**
   * Upload invoice image for OCR
   * Uses longer timeout for file uploads
   */
  async uploadInvoiceImage(formData, options = {}) {
    return apiClient.post('/invoices/upload', formData, {
      timeout: options.timeout || 120000, // 2 minutes for file uploads
      ...options
    });
  },

  /**
   * Bulk upload / process invoices by IDs
   */
  async bulkUpload(data) {
    return apiClient.post('/invoices/bulk-upload', data);
  },

  /**
   * Bulk sync invoices to accounting software
   */
  async bulkSync(data) {
    return apiClient.post('/invoices/bulk-sync', data);
  },

  /**
   * Delete single invoice
   */
  async deleteInvoice(invoiceId) {
    return apiClient.delete(`/invoices/${invoiceId}`);
  },

  /**
   * Bulk delete multiple invoices
   */
  async bulkDelete(invoiceIds) {
    return apiClient.post('/invoices/bulk-delete', { invoiceIds });
  },

  /**
   * Update invoice details
   */
  async updateInvoice(invoiceId, updates) {
    return apiClient.put(`/invoices/${invoiceId}`, updates);
  },
};

/**
 * GST API
 */
export const gstAPI = {
  /**
   * Get GST dashboard
   */
  async getDashboard(params = {}) {
    // Use analytics dashboard endpoint
    return apiClient.get('/gst/analytics/dashboard', { params });
  },

  /**
   * Get GST return types
   */
  async getReturnTypes() {
    return apiClient.get('/gst-compliance/config/return-types');
  },

  /**
   * Get due returns
   */
  async getDueReturns(params = {}) {
    return apiClient.get('/gst-compliance/returns/due', { params });
  },

  /**
   * Get filed returns (mock for now)
   */
  async getFiledReturns(params = {}) {
    // Mock data since this endpoint doesn't exist yet
    return Promise.resolve({
      success: true,
      data: []
    });
  },

  /**
   * Generate GST return
   */
  async generateGSTR1(data) {
    return apiClient.post('/gst-compliance/gstr1/generate', data);
  },

  /**
   * Generate GSTR3B return
   */
  async generateGSTR3B(data) {
    return apiClient.post('/gst-compliance/gstr3b/generate', data);
  },

  /**
   * File GSTR1 return
   */
  async fileGSTR1(data) {
    return apiClient.post('/gst-compliance/gstr1/file', data);
  },

  /**
   * File GSTR3B return
   */
  async fileGSTR3B(data) {
    return apiClient.post('/gst-compliance/gstr3b/file', data);
  },

  /**
   * Verify GSTIN
   */
  async verifyGSTIN(gstin) {
    return apiClient.post('/gst/verify', { gstin });
  },

  /**
   * Bulk verify GSTINs
   */
  async bulkVerifyGSTIN(gstins, options = {}) {
    return apiClient.post('/gst/verify-bulk', { gstins, options });
  },

  /**
   * Setup automated filing
   */
  async setupAutomatedFiling(data) {
    return apiClient.post('/gst-compliance/automated-filing/setup', data);
  },

  /**
   * Get GST settings
   */
  async getSettings() {
    return apiClient.get('/gst/settings');
  },

  /**
   * Update GST settings
   */
  async updateSettings(settingsData) {
    return apiClient.put('/gst/settings', settingsData);
  },

  /**
   * Toggle GST features
   */
  async toggleFeatures(enabled) {
    const userData = apiClient.getUserData();
    const userId = userData?.id || userData?.$id || userData?.userId;
    return apiClient.post('/gst/toggle-features', { enabled, userId });
  },

  /**
   * Get OCR / GST metrics snapshot
   */
  async getMetrics() {
    return apiClient.get('/gst/metrics');
  },

  // Filing
  async prepareFiling(businessId, returnType, period, isNil = false) {
    const normalizedReturnType = returnType ? returnType.replace(/-/g, '').toUpperCase() : returnType;
    return apiClient.post('/gst/filing/prepare', { businessId, returnType: normalizedReturnType, period, isNil });
  },

  async prepareNilFiling(businessId, returnType, period) {
    return apiClient.post('/gst/filing/prepare-nil', { businessId, returnType, period });
  },

  async fileReturn(filingId) {
    return apiClient.post(`/gst/filing/${filingId}/file`);
  },

  async getFilingStatus(filingId) {
    return apiClient.get(`/gst/filing/${filingId}/status`);
  },

  // Reconciliation
  async reconcileGSTR2A(businessId, period) {
    return apiClient.post('/gst/reconciliation/gstr2a', { businessId, period });
  },

  async reconcileGSTR2B(businessId, period) {
    return apiClient.post('/gst/reconciliation/gstr2b', { businessId, period });
  },

  // E-Invoice
  async generateEInvoice(businessId, invoiceId, data) {
    return apiClient.post('/gst/e-invoice/generate', { businessId, invoiceId, ...data });
  },

  async cancelEInvoice(irn, reason, reasonCode) {
    return apiClient.post('/gst/e-invoice/cancel', { irn, reason, reasonCode });
  },

  async getEInvoice(irn) {
    return apiClient.get(`/gst/e-invoice/${irn}`);
  },

  async bulkGenerateEInvoice(businessId, invoiceIds) {
    return apiClient.post('/gst/e-invoice/bulk-generate', { businessId, invoiceIds });
  },

  // E-Way Bill
  async generateEWayBill(businessId, invoiceId, data) {
    return apiClient.post('/gst/e-way-bill/generate', { businessId, invoiceId, ...data });
  },

  async updateEWayBill(ewbNo, data) {
    return apiClient.post('/gst/e-way-bill/update', { ewbNo, ...data });
  },

  async cancelEWayBill(ewbNo, reason, reasonCode) {
    return apiClient.post('/gst/e-way-bill/cancel', { ewbNo, reason, reasonCode });
  },

  async getEWayBill(ewbNo) {
    return apiClient.get(`/gst/e-way-bill/${ewbNo}`);
  },

  async generateEWayBillPartB(ewbNo, data) {
    return apiClient.post(`/gst/e-way-bill/${ewbNo}/part-b`, data);
  },

  // Ledgers
  async getCashLedger(businessId, params) {
    return apiClient.get(`/gst/ledger/${businessId}/cash`, { params });
  },

  async getITCLedger(businessId, params) {
    return apiClient.get(`/gst/ledger/${businessId}/itc`, { params });
  },

  async getLiabilityLedger(businessId, params) {
    return apiClient.get(`/gst/ledger/${businessId}/liability`, { params });
  },
};

/**
 * Reports API
 */
export const reportsAPI = {
  /**
   * Get report templates
   */
  async getTemplates() {
    return apiClient.get('/analytics/reports/templates');
  },

  /**
   * Get sales report
   */
  async getSalesReport(params = {}) {
    return apiClient.get('/reports/sales', { params });
  },

  /**
   * Get inventory report
   */
  async getInventoryReport(params = {}) {
    return apiClient.get('/reports/inventory', { params });
  },

  /**
   * Get purchase report
   */
  async getPurchaseReport(params = {}) {
    return apiClient.get('/reports/purchases', { params });
  },

  /**
   * Get customer report
   */
  async getCustomerReport(params = {}) {
    return apiClient.get('/reports/customers', { params });
  },

  /**
   * Export report
   */
  async exportReport(reportType, format, params = {}) {
    return apiClient.post(`/reports/${reportType}/export/${format}`, params);
  },
};

export const subscriptionsAPI = {
  async getPlans(userType) {
    const params = userType ? { userType } : {};
    return apiClient.get('/subscriptions/plans', { params });
  },
  async getCurrent() {
    return apiClient.get('/subscriptions/current');
  },
  async create(data) {
    return apiClient.post('/subscriptions/create', data);
  },
  async upgrade(data) {
    return apiClient.post('/subscriptions/upgrade', data);
  },
  async downgrade(data) {
    return apiClient.post('/subscriptions/downgrade', data);
  },
  async cancel(data) {
    return apiClient.post('/subscriptions/cancel', data);
  },
  async getUsage() {
    return apiClient.get('/subscriptions/usage');
  },
  async getBillingHistory() {
    return apiClient.get('/subscriptions/billing-history');
  },
  async comparePlans() {
    return apiClient.get('/subscriptions/compare');
  },
  async validateUpgrade(data) {
    return apiClient.post('/subscriptions/validate-upgrade', data);
  },
  async paymentRetry() {
    return apiClient.post('/subscriptions/payment-retry');
  },
};

export const multiLocationAPI = {
  async createLocation(data) {
    return apiClient.post('/multi-location/locations', data);
  },
  async getLocations(params = {}) {
    return apiClient.get('/multi-location/locations', { params });
  },
  async getLocation(locationId) {
    return apiClient.get(`/multi-location/locations/${locationId}`);
  },
  async updateLocation(locationId, data) {
    return apiClient.put(`/multi-location/locations/${locationId}`, data);
  },
  async deleteLocation(locationId) {
    return apiClient.delete(`/multi-location/locations/${locationId}`);
  },
  async getMultiLocationInventory(params = {}) {
    return apiClient.get('/multi-location/inventory/multi-location', { params });
  },
  async checkInventoryAvailability(productId, quantity = 1) {
    return apiClient.get(`/multi-location/inventory/availability/${productId}`, { params: { quantity } });
  },
  async createTransfer(data) {
    return apiClient.post('/multi-location/transfers', data);
  },
  async getTransfers(params = {}) {
    return apiClient.get('/multi-location/transfers', { params });
  },
  async getTransfer(transferId) {
    return apiClient.get(`/multi-location/transfers/${transferId}`);
  },
  async approveTransfer(transferId) {
    return apiClient.put(`/multi-location/transfers/${transferId}/approve`);
  },
  async completeTransfer(transferId) {
    return apiClient.put(`/multi-location/transfers/${transferId}/complete`);
  },
  async cancelTransfer(transferId) {
    return apiClient.put(`/multi-location/transfers/${transferId}/cancel`);
  },
  async getLocationSalesReport(params = {}) {
    return apiClient.get('/multi-location/reports/location-sales', { params });
  },
  async getLocationPerformance(params = {}) {
    return apiClient.get('/multi-location/analytics/location-performance', { params });
  },
  async getTransferAnalytics(params = {}) {
    return apiClient.get('/multi-location/analytics/transfer-analytics', { params });
  },
  async getLocationTypes() {
    return apiClient.get('/multi-location/config/location-types');
  },
  async getTransferStatuses() {
    return apiClient.get('/multi-location/config/transfer-statuses');
  },
};

export const localizationAPI = {
  async getLanguages() {
    return apiClient.get('/localization/languages');
  },
  async getTranslations(params = {}) {
    return apiClient.get('/localization/translations', { params });
  },
  async translate(data) {
    return apiClient.post('/localization/translate', data);
  },
  async formatNumber(data) {
    return apiClient.post('/localization/format-number', data);
  },
  async formatCurrency(data) {
    return apiClient.post('/localization/format-currency', data);
  },
  async formatDate(data) {
    return apiClient.post('/localization/format-date', data);
  },
  async detectLanguage() {
    return apiClient.get('/localization/detect-language');
  },
  async getCulturalInfo() {
    return apiClient.get('/localization/cultural-info');
  },
  async getConfig() {
    return apiClient.get('/localization/config');
  },
};

export const dailySummaryAPI = {
  async getDailySummary(date) {
    return apiClient.get(`/daily-summary/${date}`);
  },
  async getDailySummaryRange(startDate, endDate) {
    return apiClient.get('/daily-summary/range', { params: { start: startDate, end: endDate } });
  },
  async computeDailySummary(date) {
    return apiClient.post('/daily-summary/compute', { date });
  },
};

export const paymentAPI = {
  async recordPayment(data) {
    return apiClient.post('/payments', data);
  },
  async getPaymentHistory(customerId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    return apiClient.get(`/payments/customer/${customerId}${params.toString() ? `?${params.toString()}` : ''}`);
  },
  async getPaymentsByDate(date) {
    return apiClient.get('/payments', { params: { date } });
  },
};

// Payment Reminders API
export const reminderAPI = {
  async sendReminder(data) {
    return apiClient.post('/reminders', data);
  },
  async sendBulkReminders(data) {
    return apiClient.post('/reminders/bulk', data);
  },
  async getReminderHistory(customerId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    return apiClient.get(`/reminders/customer/${customerId}${params.toString() ? `?${params.toString()}` : ''}`);
  },
  async getReminderTemplates() {
    return apiClient.get('/reminders/templates');
  },
};

// Export API
export const exportAPI = {
  async exportInvoice(invoiceId, format = 'pdf') {
    return apiClient.post(`/export/invoice/${invoiceId}`, { format });
  },
  async exportDailySummary(date, format = 'pdf') {
    return apiClient.post(`/export/daily-summary/${date}`, { format });
  },
  async exportReport(type, format = 'pdf', options = {}) {
    return apiClient.post('/export/report', { type, format, ...options });
  },
};

// QR Payment API
export const qrPaymentAPI = {
  async generateQR(data) {
    return apiClient.post('/qr-payments/generate', data);
  },
  async getQRStatus(qrId) {
    return apiClient.get(`/qr-payments/${qrId}/status`);
  },
  async markAsPaid(qrId, data = {}) {
    return apiClient.post(`/qr-payments/${qrId}/mark-paid`, data);
  },
  async getPaymentLink(customerId, amount, upiId) {
    return qrPaymentAPI.generateQR({ customerId, amount, upiId });
  },
};

// Bulk Operations API
export const bulkAPI = {
  async bulkInvoices(data) {
    return apiClient.post('/bulk/invoices', data);
  },
  async bulkPayments(data) {
    return apiClient.post('/bulk/payments', data);
  },
  async bulkImportCustomers(file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/bulk/import/customers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  async bulkImportProducts(file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/bulk/import/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  async bulkReminders(data) {
    return apiClient.post('/bulk/reminders', data);
  },
};

/**
 * Upload API
 */
export const uploadAPI = {
  /**
   * Upload file
   */
  async uploadFile(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.category) {
      formData.append('category', options.category);
    }
    
    return apiClient.post('/upload', formData);
  },

  /**
   * Upload multiple files
   */
  async uploadFiles(files, options = {}) {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });
    
    Object.keys(options).forEach(key => {
      formData.append(key, options[key]);
    });

    return apiClient.post('/upload/batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

/**
 * OCR API
 */
export const ocrAPI = {
  /**
   * Process invoice OCR
   */
  async processInvoice(file, options = {}) {
    const formData = new FormData();
    formData.append('invoice', file);
    
    if (options.language) {
      formData.append('language', options.language);
    }
    
    return apiClient.post('/ocr/invoice', formData);
  },

  /**
   * Get OCR history
   */
  async getHistory(params = {}) {
    return apiClient.get('/ocr/history', { params });
  },
};

/**
 * Notifications API
 */
export const notificationsAPI = {
  /**
   * Get notifications
   */
  async getNotifications(params = {}) {
    return apiClient.get('/notifications', { params });
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    return apiClient.patch(`/notifications/${notificationId}/read`);
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    return apiClient.patch('/notifications/read-all');
  },

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    return apiClient.delete(`/notifications/${notificationId}`);
  },
};

/**
 * Profile API
 */
export const profileAPI = {
  /**
   * Get user profile
   */
  async getProfile() {
    return apiClient.get('/profile');
  },

  /**
   * Update user profile
   */
  updateProfile: async (profileData) => {
    return apiClient.put('/profile', profileData);
  },

  /**
   * Get user preferences
   */
  async getPreferences() {
    return apiClient.get('/profile/preferences');
  },

  /**
   * Update user preferences (notifications, display, privacy)
   */
  async updatePreferences(preferencesData) {
    return apiClient.put('/profile/preferences', preferencesData);
  },

  /**
   * Upload profile picture
   */
  async uploadProfilePicture(file) {
    const formData = new FormData();
    formData.append('profilePicture', file);
    
    return apiClient.post('/profile/picture', formData);
  },
};

/**
 * Public Plans API
 * No authentication required - for pricing page
 */
export const publicPlansAPI = {
  /**
   * Get all active subscription plans
   * @param {string} userType - 'vendor' or 'ca'
   * @returns {Promise} Plans data
   */
  async getPlans(userType) {
    const params = userType ? { userType } : {};
    return apiClient.get('/public/plans', { params });
  },
};

/**
 * Sessions API
 * Handles device sessions and device limits
 */
export const sessionsAPI = {
  /**
   * Get active sessions for the current user
   * @returns {Promise} Active sessions with device limit info
   */
  async getActiveSessions() {
    return apiClient.get('/sessions/active');
  },

  /**
   * Get device limit for the current user
   * @returns {Promise} Device limit info
   */
  async getDeviceLimit() {
    return apiClient.get('/sessions/limit');
  },

  /**
   * Revoke a specific session
   * @param {string} sessionId - Session ID to revoke
   * @returns {Promise} Revocation result
   */
  async revokeSession(sessionId) {
    return apiClient.delete(`/sessions/${sessionId}`);
  },
};

// Export the main API client and individual API modules
export const api = {
  client: apiClient,
  auth: authAPI,
  ca: caAPI,
  analytics: analyticsAPI,
  business: businessAPI,
  vendorTeam: vendorTeamAPI,
  inventory: inventoryAPI,
  orders: ordersAPI,
  customers: customersAPI,
  suppliers: suppliersAPI,
  purchases: purchasesAPI,
  gst: gstAPI,
  reports: reportsAPI,
  upload: uploadAPI,
  ocr: ocrAPI,
  invoices: invoicesAPI,
  notifications: notificationsAPI,
  profile: profileAPI,
  subscriptions: subscriptionsAPI,
  multiLocation: multiLocationAPI,
  localization: localizationAPI,
  bulkImport: bulkImportAPI,
  publicPlans: publicPlansAPI,
  sessions: sessionsAPI,
  
  // Convenience methods for backward compatibility
  getDashboard: (params) => analyticsAPI.getDashboard(params),
  getProfile: () => businessAPI.getProfile(),
  updateProfile: (data) => businessAPI.updateProfile(data),
  getSettings: () => businessAPI.getSettings(),
  updateSettings: (data) => businessAPI.updateSettings(data),
  
  getBillingInfo: () => businessAPI.getProfile(),
  getInvoices: () => subscriptionsAPI.getBillingHistory(),
  getPaymentMethods: () => businessAPI.getSettings(),
  getSubscription: () => subscriptionsAPI.getCurrent(),
  updateBillingInfo: (data) => businessAPI.updateProfile(data),
  removePaymentMethod: (id) => businessAPI.updateSettings({ removePaymentMethod: id }),
  
  // User & Profile methods
  getUserPreferences: () => profileAPI.getProfile(),
  getActiveSessions: () => sessionsAPI.getActiveSessions(),
  getDeviceLimit: () => sessionsAPI.getDeviceLimit(),
  revokeSession: (id) => sessionsAPI.revokeSession(id),
  changePassword: (data) => authAPI.changePassword(data),
  updateUserPreferences: (data) => profileAPI.updateProfile(data),
  terminateSession: (id) => sessionsAPI.revokeSession(id),
  terminateAllSessions: () => authAPI.logout(),
  updatePassword: (data) => authAPI.changePassword(data),
  
  // GST methods
  getReturnTypes: () => gstAPI.getReturnTypes(),
  getDueReturns: (params) => gstAPI.getDueReturns(params),
  getFiledReturns: (params) => gstAPI.getFiledReturns(params),
  verifyGSTIN: (gstin) => gstAPI.verifyGSTIN(gstin),
  generateGSTReturn: (data) => {
    // Route to appropriate return type
    if (data.type === 'GSTR1') {
      return gstAPI.generateGSTR1(data);
    } else if (data.type === 'GSTR3B') {
      return gstAPI.generateGSTR3B(data);
    } else {
      return Promise.reject(new Error(`Unsupported return type: ${data.type}`));
    }
  },
  setupAutomatedFiling: (data) => gstAPI.setupAutomatedFiling(data),
  
  // Usage methods
  getUsageStats: (params) => analyticsAPI.getDashboard(params),
  getUsageHistory: (params) => analyticsAPI.getDashboard(params),
  getUsageQuotas: () => analyticsAPI.getDashboard(),
  getUsageAlerts: () => notificationsAPI.getNotifications(),
  exportUsageData: (params) => reportsAPI.exportReport('usage', 'csv', params),
  
  getLocations: (params) => multiLocationAPI.getLocations(params),
  getInventory: (params) => multiLocationAPI.getMultiLocationInventory(params),
  getInventoryTransfers: (params) => multiLocationAPI.getTransfers(params),
  addLocation: (data) => multiLocationAPI.createLocation(data),
  updateLocation: (id, data) => multiLocationAPI.updateLocation(id, data),
  deleteLocation: (id) => multiLocationAPI.deleteLocation(id),
  transferInventory: (data) => multiLocationAPI.createTransfer(data),
  
  // Analytics methods
  getReportTemplates: () => apiClient.get('/advanced-analytics/reports/templates'),
  getBusinessInsights: (start, end, detailed) => analyticsAPI.getAdvancedAnalytics({ start, end, detailed }),
  getSalesForecast: () => analyticsAPI.getSalesAnalytics(),
  generateReport: (templateId, params) => reportsAPI.exportReport(templateId, 'pdf', params),
  
  // Daily Summary methods
  getDailySummary: (date) => dailySummaryAPI.getDailySummary(date),
  getDailySummaryRange: (startDate, endDate) => dailySummaryAPI.getDailySummaryRange(startDate, endDate),
  computeDailySummary: (date) => dailySummaryAPI.computeDailySummary(date),
  
  // Payment methods
  recordPayment: (data) => paymentAPI.recordPayment(data),
  getPaymentHistory: (customerId, options) => paymentAPI.getPaymentHistory(customerId, options),
  getPaymentsByDate: (date) => paymentAPI.getPaymentsByDate(date),
  
  // Customer balances
  getCustomerBalances: (date) => customersAPI.getCustomerBalances(date),
  
  // Payment Reminders
  sendReminder: (data) => reminderAPI.sendReminder(data),
  sendBulkReminders: (data) => reminderAPI.sendBulkReminders(data),
  getReminderHistory: (customerId, options) => reminderAPI.getReminderHistory(customerId, options),
  getReminderTemplates: () => reminderAPI.getReminderTemplates(),
  
  // Export
  exportInvoice: (invoiceId, format) => exportAPI.exportInvoice(invoiceId, format),
  exportDailySummary: (date, format) => exportAPI.exportDailySummary(date, format),
  exportReport: (type, format, options) => exportAPI.exportReport(type, format, options),
  
  // QR Payments
  generateQR: (data) => qrPaymentAPI.generateQR(data),
  getQRStatus: (qrId) => qrPaymentAPI.getQRStatus(qrId),
  markQRAsPaid: (qrId, data) => qrPaymentAPI.markAsPaid(qrId, data),
  
  // Bulk Operations
  bulkInvoices: (data) => bulkAPI.bulkInvoices(data),
  bulkPayments: (data) => bulkAPI.bulkPayments(data),
  bulkImportCustomers: (file) => bulkAPI.bulkImportCustomers(file),
  bulkImportProducts: (file) => bulkAPI.bulkImportProducts(file),
  bulkReminders: (data) => bulkAPI.bulkReminders(data),
};

// Default export
export default api;
